import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Clock, Calendar, MapPin, Plus, Check, X, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, parseISO } from 'date-fns';

export function ChangeRequests({ meetingId, meetingData, onChangeApplied }) {
  const { api, member, isChair } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRequest, setNewRequest] = useState({
    request_type: 'date',
    requested_value: '',
    reason: ''
  });

  const fetchRequests = useCallback(async () => {
    try {
      const response = await api().get(`/meetings/${meetingId}/change-requests`);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch change requests:', error);
    } finally {
      setLoading(false);
    }
  }, [api, meetingId]);

  useEffect(() => {
    if (meetingId) {
      fetchRequests();
    }
  }, [fetchRequests, meetingId]);

  const handleCreateRequest = async () => {
    if (!newRequest.requested_value) {
      toast.error('Please enter the requested change');
      return;
    }

    setCreating(true);
    try {
      await api().post(`/meetings/${meetingId}/change-requests`, newRequest);
      setCreateDialogOpen(false);
      setNewRequest({ request_type: 'date', requested_value: '', reason: '' });
      toast.success('Change request submitted!');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setCreating(false);
    }
  };

  const handleReviewRequest = async (requestId, action) => {
    try {
      await api().put(`/change-requests/${requestId}`, { action });
      toast.success(`Request ${action}d`);
      fetchRequests();
      if (action === 'approve') {
        onChangeApplied?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} request`);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'date': return <Calendar className="h-4 w-4" />;
      case 'time': return <Clock className="h-4 w-4" />;
      case 'location': return <MapPin className="h-4 w-4" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-500">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCurrentValue = () => {
    switch (newRequest.request_type) {
      case 'date': return meetingData?.date;
      case 'time': return meetingData?.time || 'Not set';
      case 'location': return meetingData?.location_details || meetingData?.meeting_link || 'Not set';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const historyRequests = requests.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-4" data-testid="change-requests">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Change Requests</h4>
          <p className="text-sm text-muted-foreground">
            Request changes to the meeting date, time, or location
          </p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="request-change-btn">
              <Plus className="h-4 w-4" />
              Request Change
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request a Change</DialogTitle>
              <DialogDescription>
                Submit a request to change the meeting details. The chair will review it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>What do you want to change?</Label>
                <Select
                  value={newRequest.request_type}
                  onValueChange={(value) => setNewRequest({ ...newRequest, request_type: value, requested_value: '' })}
                >
                  <SelectTrigger data-testid="change-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="time">Time</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 rounded-lg bg-muted text-sm">
                <span className="text-muted-foreground">Current: </span>
                <span className="font-medium">{getCurrentValue()}</span>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="value">
                  New {newRequest.request_type === 'date' ? 'Date' : newRequest.request_type === 'time' ? 'Time' : 'Location'}
                </Label>
                {newRequest.request_type === 'date' ? (
                  <Input
                    id="value"
                    type="date"
                    value={newRequest.requested_value}
                    onChange={(e) => setNewRequest({ ...newRequest, requested_value: e.target.value })}
                    data-testid="change-value-input"
                  />
                ) : newRequest.request_type === 'time' ? (
                  <Input
                    id="value"
                    type="time"
                    value={newRequest.requested_value}
                    onChange={(e) => setNewRequest({ ...newRequest, requested_value: e.target.value })}
                    data-testid="change-value-input"
                  />
                ) : (
                  <Input
                    id="value"
                    placeholder="Address or meeting link"
                    value={newRequest.requested_value}
                    onChange={(e) => setNewRequest({ ...newRequest, requested_value: e.target.value })}
                    data-testid="change-value-input"
                  />
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Why do you need this change?"
                  value={newRequest.reason}
                  onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
                  data-testid="change-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRequest} disabled={creating} data-testid="submit-change-btn">
                {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending requests */}
      {pendingRequests.length > 0 && (
        <div className="space-y-2">
          {pendingRequests.map((req) => (
            <Card key={req.id} className="border-yellow-500/30 bg-yellow-500/5" data-testid={`request-${req.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getTypeIcon(req.request_type)}
                      <span className="font-medium capitalize">{req.request_type} Change</span>
                      {getStatusBadge(req.status)}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm mb-2">
                      <span className="text-muted-foreground">{req.current_value || 'Not set'}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{req.requested_value}</span>
                    </div>
                    
                    {req.reason && (
                      <p className="text-sm text-muted-foreground mb-2">
                        "{req.reason}"
                      </p>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      Requested by {req.requester_name} • {formatDistanceToNow(parseISO(req.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  
                  {isChair && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleReviewRequest(req.id, 'approve')}
                        className="gap-1"
                        data-testid={`approve-${req.id}`}
                      >
                        <Check className="h-4 w-4" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReviewRequest(req.id, 'reject')}
                        className="text-destructive"
                        data-testid={`reject-${req.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* History */}
      {historyRequests.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-sm font-medium text-muted-foreground">History</h5>
          {historyRequests.slice(0, 3).map((req) => (
            <div key={req.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 text-sm">
              <div className="flex items-center gap-2">
                {getTypeIcon(req.request_type)}
                <span className="capitalize">{req.request_type}</span>
                <span className="text-muted-foreground">→</span>
                <span>{req.requested_value}</span>
              </div>
              {getStatusBadge(req.status)}
            </div>
          ))}
        </div>
      )}

      {pendingRequests.length === 0 && historyRequests.length === 0 && (
        <div className="text-center p-4 text-sm text-muted-foreground">
          No change requests yet
        </div>
      )}
    </div>
  );
}
